import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalBusiness',
  name: 'NEMT Services',
  description:
    'Professional non-emergency medical transportation (NEMT) services with wheelchair accessible vehicles, ambulatory transport, and stretcher services. Medicaid and Medicare accepted.',
  url: siteUrl,
  telephone: '(401) 903-9036',
  priceRange: '$$',
  medicalSpecialty: 'Non-Emergency Medical Transportation',
  availableService: [
    {
      '@type': 'MedicalTherapy',
      name: 'Wheelchair Accessible Transport',
    },
    {
      '@type': 'MedicalTherapy',
      name: 'Ambulatory Transport',
    },
    {
      '@type': 'MedicalTherapy',
      name: 'Stretcher Transport',
    },
    {
      '@type': 'MedicalTherapy',
      name: 'Bariatric Transport',
    },
  ],
  sameAs: ['https://facebook.com', 'https://linkedin.com'],
};

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'NEMT Services',
  url: siteUrl,
  description:
    'Professional non-emergency medical transportation services. Medicaid and Medicare accepted.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteUrl}/?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Non-Emergency Medical Transportation',
  provider: {
    '@type': 'MedicalBusiness',
    name: 'NEMT Services',
    url: siteUrl,
  },
  description:
    'Specialized transport for individuals who need assistance getting to and from medical appointments. Services include wheelchair accessible vehicles, ambulatory transport, stretcher transport, and bariatric transport.',
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'NEMT Transportation Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Wheelchair Accessible Transport',
          description:
            'Safe and comfortable transport for patients using wheelchairs or mobility devices.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Ambulatory Transport',
          description:
            'Transport for patients who can walk but need assistance getting to medical appointments.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Stretcher Transport',
          description:
            'Non-emergency stretcher transport for patients who cannot sit upright.',
        },
      },
    ],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Does Medicaid cover non-emergency medical transportation?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, Medicaid covers non-emergency medical transportation (NEMT) for eligible beneficiaries who need transportation to and from covered medical services.',
      },
    },
    {
      '@type': 'Question',
      name: 'What types of vehicles are available for NEMT services?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We offer wheelchair accessible vans, ambulatory vehicles, stretcher vans, and bariatric transport vehicles to accommodate all mobility needs.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I schedule a non-emergency medical transportation ride?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can schedule a ride by calling (401) 903-9036 or by filling out our online booking form on our website.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Medicare cover NEMT services?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Traditional Medicare generally does not cover non-emergency medical transportation. However, some Medicare Advantage plans may include NEMT benefits. Contact us to verify your coverage.',
      },
    },
    {
      '@type': 'Question',
      name: 'How far in advance should I book my medical transportation?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We recommend booking at least 48–72 hours in advance to ensure availability. Same-day rides may be available depending on your location and service demand.',
      },
    },
  ],
};

const schemas = [organizationSchema, webSiteSchema, serviceSchema, faqSchema];

export default function SeoJsonLd() {
  useEffect(() => {
    schemas.forEach((schema, index) => {
      const id = `jsonld-schema-${index}`;
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    });

    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`jsonld-schema-${index}`);
        if (script) script.remove();
      });
    };
  }, []);

  return null;
}
